import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { showSuccess, showError } from '@/lib/toast';
import {
  Users, Plus, Crown, Shield, Eye, UserPlus, Trash2, Settings, Server, ChevronRight,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface Team {
  id: string;
  name: string;
  slug: string;
  description?: string;
  plan: string;
  memberCount: number;
  instanceCount: number;
  myRole: string;
  createdAt: string;
}

interface TeamMember {
  id: string;
  userId: string;
  email: string;
  name?: string;
  avatarUrl?: string;
  role: string;
  joinedAt: string;
}

interface TeamInstance {
  id: string;
  name: string;
  status: string;
  provider: string;
  region: string;
  aiModel?: string;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                         */
/* ------------------------------------------------------------------ */

type TabKey = 'members' | 'instances' | 'settings';

const TABS: { key: TabKey; i18nKey: string }[] = [
  { key: 'members', i18nKey: 'teams.members' },
  { key: 'instances', i18nKey: 'teams.instances' },
  { key: 'settings', i18nKey: 'teams.settings' },
];

const ROLE_STYLES: Record<string, { bg: string; text: string; icon: typeof Crown }> = {
  owner:  { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', icon: Crown },
  admin:  { bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-400',     icon: Shield },
  member: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', icon: Users },
  viewer: { bg: 'bg-gray-100 dark:bg-gray-800/50',    text: 'text-gray-600 dark:text-gray-400',     icon: Eye },
};

const ROLE_OPTIONS = ['admin', 'member', 'viewer'] as const;

const STATUS_COLORS: Record<string, string> = {
  running: 'bg-emerald-500',
  stopped: 'bg-gray-400',
  provisioning: 'bg-yellow-500',
  error: 'bg-red-500',
  starting: 'bg-blue-500',
  stopping: 'bg-orange-500',
};

/* ------------------------------------------------------------------ */
/*  Helper: Role badge                                                */
/* ------------------------------------------------------------------ */

function RoleBadge({ role }: { role: string }) {
  const style = ROLE_STYLES[role] || ROLE_STYLES.viewer;
  const Icon = style.icon;
  return (
    <Badge className={cn('gap-1 border-transparent', style.bg, style.text)}>
      <Icon className="h-3 w-3" />
      {role}
    </Badge>
  );
}

/* ------------------------------------------------------------------ */
/*  Helper: Plan badge                                                */
/* ------------------------------------------------------------------ */

function PlanBadge({ plan }: { plan: string }) {
  const colors: Record<string, string> = {
    free: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    pro: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    enterprise: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  };
  return (
    <Badge className={cn('border-transparent capitalize', colors[plan] || colors.free)}>
      {plan}
    </Badge>
  );
}

/* ================================================================== */
/*  Main Component                                                    */
/* ================================================================== */

export function Teams() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  /* ----- state ----- */
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('members');
  const [createOpen, setCreateOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);

  // Create team form
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');

  // Add member form
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState<string>('member');

  // Settings form (edit)
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  /* ---------------------------------------------------------------- */
  /*  Queries                                                         */
  /* ---------------------------------------------------------------- */

  const { data: teamsData, isLoading: teamsLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: async () => {
      const { data } = await api.get('/teams');
      return data;
    },
  });

  const teams: Team[] = teamsData?.teams || [];

  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['teams', selectedTeam?.id, 'members'],
    queryFn: async () => {
      const { data } = await api.get(`/teams/${selectedTeam!.id}/members`);
      return data;
    },
    enabled: !!selectedTeam,
  });

  const members: TeamMember[] = membersData?.members || [];

  const { data: instancesData, isLoading: instancesLoading } = useQuery({
    queryKey: ['teams', selectedTeam?.id, 'instances'],
    queryFn: async () => {
      const { data } = await api.get(`/teams/${selectedTeam!.id}/instances`);
      return data;
    },
    enabled: !!selectedTeam,
  });

  const instances: TeamInstance[] = instancesData?.instances || [];

  /* ---------------------------------------------------------------- */
  /*  Mutations                                                       */
  /* ---------------------------------------------------------------- */

  const createTeam = useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/teams', { name: newName, description: newDesc });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      showSuccess(t('teams.createSuccess'));
      setCreateOpen(false);
      setNewName('');
      setNewDesc('');
    },
    onError: () => {
      showError(t('teams.createError'));
    },
  });

  const updateTeam = useMutation({
    mutationFn: async () => {
      const { data } = await api.patch(`/teams/${selectedTeam!.id}`, {
        name: editName,
        description: editDesc,
      });
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      showSuccess(t('teams.updateSuccess'));
      const updated = data?.team || { ...selectedTeam, name: editName, description: editDesc };
      setSelectedTeam(updated);
    },
    onError: () => {
      showError(t('teams.updateError'));
    },
  });

  const deleteTeam = useMutation({
    mutationFn: async () => {
      await api.delete(`/teams/${selectedTeam!.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      showSuccess(t('teams.deleteSuccess'));
      setSelectedTeam(null);
      setDeleteConfirmOpen(false);
    },
    onError: () => {
      showError(t('teams.deleteError'));
    },
  });

  const addMember = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/teams/${selectedTeam!.id}/members`, {
        email: memberEmail,
        role: memberRole,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', selectedTeam?.id, 'members'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      showSuccess(t('teams.addMemberSuccess'));
      setAddMemberOpen(false);
      setMemberEmail('');
      setMemberRole('member');
    },
    onError: () => {
      showError(t('teams.addMemberError'));
    },
  });

  const removeMember = useMutation({
    mutationFn: async (memberId: string) => {
      await api.delete(`/teams/${selectedTeam!.id}/members/${memberId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', selectedTeam?.id, 'members'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      showSuccess(t('teams.removeMemberSuccess'));
    },
    onError: () => {
      showError(t('teams.removeMemberError'));
    },
  });

  const updateMemberRole = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: string }) => {
      await api.patch(`/teams/${selectedTeam!.id}/members/${memberId}/role`, { role });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', selectedTeam?.id, 'members'] });
      showSuccess(t('teams.updateRoleSuccess'));
    },
    onError: () => {
      showError(t('teams.updateRoleError'));
    },
  });

  /* ---------------------------------------------------------------- */
  /*  Handlers                                                        */
  /* ---------------------------------------------------------------- */

  const handleSelectTeam = (team: Team) => {
    setSelectedTeam(team);
    setActiveTab('members');
    setEditName(team.name);
    setEditDesc(team.description || '');
  };

  const handleBackToList = () => {
    setSelectedTeam(null);
  };

  const canManageMembers = selectedTeam && ['owner', 'admin'].includes(selectedTeam.myRole);
  const isOwner = selectedTeam?.myRole === 'owner';

  /* ================================================================ */
  /*  Render: Team Detail View                                        */
  /* ================================================================ */

  if (selectedTeam) {
    return (
      <div className="space-y-6">
        {/* Back button + Team header */}
        <div>
          <button
            onClick={handleBackToList}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors mb-3 flex items-center gap-1"
          >
            <ChevronRight className="h-3.5 w-3.5 rotate-180" />
            {t('teams.backToTeams')}
          </button>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Users className="h-5 w-5 text-emerald-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold">{selectedTeam.name}</h1>
                <PlanBadge plan={selectedTeam.plan} />
                <RoleBadge role={selectedTeam.myRole} />
              </div>
              {selectedTeam.description && (
                <p className="text-sm text-muted-foreground mt-0.5">
                  {selectedTeam.description}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'px-4 py-2.5 text-sm font-medium transition-colors relative',
                activeTab === tab.key
                  ? 'text-emerald-600'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t(tab.i18nKey)}
              {activeTab === tab.key && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 rounded-t" />
              )}
            </button>
          ))}
        </div>

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="space-y-4">
            {canManageMembers && (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20"
                  onClick={() => setAddMemberOpen(true)}
                >
                  <UserPlus className="h-4 w-4" />
                  {t('teams.addMember')}
                </Button>
              </div>
            )}

            {membersLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
                ))}
              </div>
            ) : members.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mb-3">
                    <Users className="h-7 w-7 text-muted-foreground/40" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('teams.noMembers')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {members.map((member) => (
                  <Card
                    key={member.id}
                    className="transition-all hover:shadow-sm hover:border-emerald-200 dark:hover:border-emerald-800"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {/* Avatar */}
                          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 shadow-sm">
                            {member.avatarUrl ? (
                              <img
                                src={member.avatarUrl}
                                alt=""
                                className="h-9 w-9 rounded-full object-cover"
                              />
                            ) : (
                              (member.name || member.email).charAt(0).toUpperCase()
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">
                              {member.name || member.email}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {member.email}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {/* Role badge with inline selector for admins */}
                          {canManageMembers && member.role !== 'owner' ? (
                            <select
                              value={member.role}
                              onChange={(e) =>
                                updateMemberRole.mutate({
                                  memberId: member.id,
                                  role: e.target.value,
                                })
                              }
                              className="text-xs border rounded-md px-2 py-1 bg-background focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            >
                              {ROLE_OPTIONS.map((r) => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <RoleBadge role={member.role} />
                          )}

                          {/* Remove button */}
                          {canManageMembers && member.role !== 'owner' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                              onClick={() => removeMember.mutate(member.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Instances Tab */}
        {activeTab === 'instances' && (
          <div className="space-y-4">
            {instancesLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
                ))}
              </div>
            ) : instances.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mb-3">
                    <Server className="h-7 w-7 text-muted-foreground/40" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('teams.noInstances')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {instances.map((instance) => (
                  <Card
                    key={instance.id}
                    className="transition-all hover:shadow-sm hover:border-emerald-200 dark:hover:border-emerald-800"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div
                              className={cn(
                                'h-3 w-3 rounded-full',
                                STATUS_COLORS[instance.status] || 'bg-gray-400',
                              )}
                            />
                            {instance.status === 'running' && (
                              <div
                                className={cn(
                                  'absolute inset-0 h-3 w-3 rounded-full animate-ping opacity-40',
                                  STATUS_COLORS[instance.status],
                                )}
                              />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm">{instance.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {instance.provider} &middot; {instance.region}
                              {instance.aiModel && <> &middot; {instance.aiModel}</>}
                            </p>
                          </div>
                        </div>
                        <Badge
                          variant={
                            instance.status === 'running'
                              ? 'success'
                              : instance.status === 'error'
                                ? 'destructive'
                                : 'secondary'
                          }
                          className="capitalize"
                        >
                          {instance.status}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6 max-w-lg">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  {t('teams.teamSettings')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">
                    {t('teams.teamName')}
                  </label>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder={t('teams.teamNamePlaceholder')}
                    disabled={!canManageMembers}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">
                    {t('teams.description')}
                  </label>
                  <Input
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    placeholder={t('teams.descriptionPlaceholder')}
                    disabled={!canManageMembers}
                  />
                </div>
                {canManageMembers && (
                  <Button
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20"
                    onClick={() => updateTeam.mutate()}
                    disabled={updateTeam.isPending || !editName.trim()}
                  >
                    {updateTeam.isPending
                      ? t('teams.saving')
                      : t('teams.saveChanges')}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Danger Zone */}
            {isOwner && (
              <Card className="border-red-200 dark:border-red-900">
                <CardHeader>
                  <CardTitle className="text-base text-red-600 dark:text-red-400">
                    {t('teams.dangerZone')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('teams.deleteDescription')}
                  </p>
                  <Button
                    variant="destructive"
                    className="gap-2"
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    <Trash2 className="h-4 w-4" />
                    {t('teams.deleteTeam')}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Add Member Dialog */}
        <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <div className="flex items-center gap-3 mb-1">
                <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <DialogTitle>
                    {t('teams.addMember')}
                  </DialogTitle>
                  <DialogDescription>
                    {t('teams.addMemberDescription')}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <Separator />

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">
                  {t('teams.email')}
                </label>
                <Input
                  type="email"
                  value={memberEmail}
                  onChange={(e) => setMemberEmail(e.target.value)}
                  placeholder="user@example.com"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">
                  {t('teams.role')}
                </label>
                <div className="flex gap-2">
                  {ROLE_OPTIONS.map((role) => {
                    const style = ROLE_STYLES[role];
                    const Icon = style.icon;
                    return (
                      <button
                        key={role}
                        onClick={() => setMemberRole(role)}
                        className={cn(
                          'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all',
                          memberRole === role
                            ? cn(style.bg, style.text, 'border-current shadow-sm')
                            : 'border-border text-muted-foreground hover:border-emerald-300',
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span className="capitalize">{role}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <Separator />

              <Button
                className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20"
                onClick={() => addMember.mutate()}
                disabled={addMember.isPending || !memberEmail.trim()}
              >
                <UserPlus className="h-4 w-4" />
                {addMember.isPending
                  ? t('teams.adding')
                  : t('teams.addMember')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600 dark:text-red-400">
                {t('teams.confirmDeletion')}
              </DialogTitle>
              <DialogDescription>
                {t('teams.deleteConfirmWithName', { name: selectedTeam.name })}
              </DialogDescription>
            </DialogHeader>

            <Separator />

            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                className="gap-2"
                onClick={() => deleteTeam.mutate()}
                disabled={deleteTeam.isPending}
              >
                <Trash2 className="h-4 w-4" />
                {deleteTeam.isPending
                  ? t('teams.deleting')
                  : t('common.delete')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  /* ================================================================ */
  /*  Render: Team List View                                          */
  /* ================================================================ */

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <Users className="h-5 w-5 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {t('teams.title')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('teams.subtitle')}
            </p>
          </div>
        </div>
        <Button
          className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="h-4 w-4" />
          {t('teams.createTeam')}
        </Button>
      </div>

      <Separator />

      {/* Loading */}
      {teamsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : teams.length === 0 ? (
        /* Empty state */
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-20">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Users className="h-8 w-8 text-muted-foreground/40" />
            </div>
            <h3 className="text-lg font-medium mb-2">
              {t('teams.noTeams')}
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-md mb-6">
              {t('teams.noTeamsDescription')}
            </p>
            <Button
              className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20"
              onClick={() => setCreateOpen(true)}
            >
              <Plus className="h-4 w-4" />
              {t('teams.createFirstTeam')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        /* Team grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map((team) => (
            <Card
              key={team.id}
              className="group cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/5 hover:border-emerald-200 dark:hover:border-emerald-800"
              onClick={() => handleSelectTeam(team)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-base font-bold leading-tight">
                      {team.name}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                      {team.slug}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-emerald-500 transition-colors flex-shrink-0 mt-0.5" />
                </div>
              </CardHeader>

              <CardContent className="space-y-3">
                {team.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                    {team.description}
                  </p>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  <PlanBadge plan={team.plan} />
                  <RoleBadge role={team.myRole} />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Users className="h-3.5 w-3.5" />
                      {t('teams.memberCount', { count: team.memberCount ?? 0 })}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Server className="h-3.5 w-3.5" />
                      {t('teams.instanceCount', { count: team.instanceCount ?? 0 })}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Team Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <DialogTitle>
                  {t('teams.createTeam')}
                </DialogTitle>
                <DialogDescription>
                  {t('teams.createTeamDescription')}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <Separator />

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">
                {t('teams.teamName')}
              </label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t('teams.teamNameExample')}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">
                {t('teams.descriptionOptional')}
              </label>
              <Input
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder={t('teams.descriptionExample')}
              />
            </div>

            <Separator />

            <Button
              className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20"
              onClick={() => createTeam.mutate()}
              disabled={createTeam.isPending || !newName.trim()}
            >
              <Plus className="h-4 w-4" />
              {createTeam.isPending
                ? t('teams.creating')
                : t('teams.createTeam')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
